import { Injectable } from '@angular/core';
import { Course } from '../../../@academy/catalog/models/Course.model';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Presential } from '../../../@academy/catalog/models/Presential.model';
import { ApiService, ParamsObject } from '../../utils/api.service';
import { HttpParams } from '@angular/common/http';
import { ExchangePresential } from '../../models/training';
import { saveAs } from 'file-saver';
import sanitizeFilename from 'sanitize-filename';
import {
  CrudArrayActionServiceMandatory,
  Param, ParamProperty,
} from '../../../shared/crud-array-action/abstract-crud-array-action.directive';

@Injectable()
export class CourseService implements CrudArrayActionServiceMandatory {

  private path = 'courses';

  private triggerEditAction: Subject<number> = new Subject<number>();

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public readonly paramAction: Param = {
    edit: {
      provide: true,
      triggerAction: this.triggerEditAction,
    },
  };

  constructor(
    private apiService: ApiService,
  ) {
  }

  public isPropertyDisplay(property: ParamProperty): boolean {
    return true;
  }

  public convertDataToSendToTrigger(data: any): number {
    return data.id;
  }

  delete(course: Course | Presential): Observable<void> {
    return this.apiService.delete(this.path + '/' + course.id);
  }

  get(id: string): Observable<ExchangePresential> {
    return this.apiService.get(this.path + '/' + id);
  }

  getAll(params: ParamsObject | HttpParams): Observable<ExchangePresential[]> {
    const httpParameters = params instanceof HttpParams ? params : new HttpParams({fromObject: params});

    return this.apiService.get(this.path, httpParameters)
      .pipe(
        map(data => {
          data['hydra:member'].map(obj => new ExchangePresential(obj));
          return data;
        }),
      );
  }

  getBudgetSelection(): Observable<ExchangePresential[]> {
    return this.getSelection('budget');
  }

  getSatisfactionSelection(): Observable<ExchangePresential[]> {
    return this.getSelection('satisfaction');
  }

  post(course): Observable<Course> {
    return this.apiService.post(this.path, course);
  }

  put(course): Observable<Course> {
    return this.apiService.put(this.path + '/' + course.id, course);
  }

  saveBudgetSelection(courses: ExchangePresential[]): Observable<ExchangePresential[]> {
    return this.apiService.post('budget-control/selections/me', courses);
  }

  saveSatisfactionSelection(courses: ExchangePresential[]): Observable<ExchangePresential[]> {
    return this.apiService.post('satisfaction/course-selections/me', courses);
  }

  public getTriggerEdit(): Observable<number> {
    return this.paramAction.edit.triggerAction.asObservable();
  }

  public sendTriggerEdit(data: number): void {
    this.paramAction.edit.triggerAction.next(data);
  }

  public extractInvoicingCode(httpParams: HttpParams = new HttpParams()): Observable<any> {
    return this.apiService.download(this.path + '/invoicingCodeExtraction', httpParams).pipe(
      map(data => saveAs(
        data,
        sanitizeFilename('courseCodeInvoicing.xls'),
        {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'},
      )),
    );
  }

  private getSelection(type: 'satisfaction' | 'budget'): Observable<ExchangePresential[]> {
    const path = type === 'satisfaction' ? 'satisfaction/course-selections/me' : 'budget-control/selections/me';

    return this.apiService.get(path)
      .pipe(
        switchMap(courses => courses.length > 0 ? this.getByIds(courses.map(course => course.id)) : of([])),
        map((data) => data['hydra:member'] ?? data),
        map((courses) => courses.map(course => new ExchangePresential(course))),
      );
  }

  private getByIds(courseIds: string[]): Observable<ExchangePresential[]> {
    let courseQuery = new HttpParams();
    courseIds.forEach(courseId => {
      courseQuery = courseQuery.append('id[]', courseId);
    });

    return this.getAll(courseQuery);
  }
}
